import { Button, LoadingSpinner } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import { useEffect } from "react";
import { useAdminContentSettingsContext } from "@/search/AdminContentSettingsContext/AdminContentSettingsContext";

import { NoResults } from "@/search/Langston/SearchResults/NoResults/NoResults.component";
import { BookmarkContextProvider } from "@/users/useBookmarkContent/useBookmarkContent";
import useGetUserFeatures from "@/users/v3/useUserFeaturesQuery";
import useEventRecordMutation from "@/users/useEventRecordMutation/useEventRecordMutation";
import { getLicenseTypeFromList } from "@/onboarding/onboarding.utils";
import { useSearchPageContext } from "@/search/Langston/SearchPageProvider";
import useGetUserExperimentSegment from "@/features/useGetUserExperimentSegment";
import { FIRST_SEARCH_COMPLETED_EVENT } from "@/search/Langston/SearchResults/SearchResults.constants";
import ContentCard from "@/components/ContentCard/";
import ContentCardV2 from "@/components/ContentCardV2/";
import { CardViewmodelAndProps } from "@/components/ContentCard/ContentCard.interface";
import { LangstonParameterSchema } from "../LangstonParameterSchema";
import {
  loadMoreButtonCss,
  resultsContainer,
  searchResultsContainerCss,
} from "./SearchResults.styles";

const ButtonWithTrackEvent = withTrackEvent(Button);

// Removes reading skills from cards for experiment.
const omitSkillsFromResults = (results: CardViewmodelAndProps[]) => {
  const newResults = [...results];
  newResults.forEach((card) => {
    if (card.metadata.content_type === "ARTICLE") {
      delete card.metadata.reading_skills;
    }
  });
  return newResults;
};

const SearchResults = () => {
  const {
    getters: { isDisabledFilters, hasNextPage, searchResults, isSearchLoading },
    handlers: { fetchNextPage, setSearchParamValue, getSearchParamValues },
  } = useSearchPageContext();
  const { postEventRecord } = useEventRecordMutation();

  const { data: userFeatures } = useGetUserFeatures();

  const { enableContentSettingsFeatures, contentSettingsGroups } =
    useAdminContentSettingsContext();

  const [removeReadingSkillChips] = useGetUserExperimentSegment(
    "remove_reading_skills_from_cards",
  );
  const [newContentCardDesigns] = useGetUserExperimentSegment(
    "new_content_card_designs",
  );
  const isExperimentRemoveReadingSkillChipsFromSearch =
    removeReadingSkillChips === "on";
  const isNewContentCardsExperimentOn = newContentCardDesigns === "on";

  // Add record to confirm the user's first assignment is complete.
  useEffect(() => {
    if (userFeatures?.includes("needs_first_content_search")) {
      const licenseType = getLicenseTypeFromList(userFeatures);
      postEventRecord({
        event_name: FIRST_SEARCH_COMPLETED_EVENT,
        data: {
          license: licenseType,
        },
      });
    }
  }, [userFeatures]);

  if (!searchResults.length && !isSearchLoading) return <NoResults />;

  const sanitizedResults = isExperimentRemoveReadingSkillChipsFromSearch
    ? omitSkillsFromResults(searchResults)
    : searchResults;

  const showLoadMoreButton =
    !isSearchLoading && hasNextPage && !isDisabledFilters;

  return (
    <>
      <div css={resultsContainer}>
        <div css={searchResultsContainerCss(isNewContentCardsExperimentOn)}>
          <BookmarkContextProvider>
            {sanitizedResults.map(
              (content: CardViewmodelAndProps, index: number) => {
                return isNewContentCardsExperimentOn ? (
                  <ContentCardV2
                    key={index}
                    {...content}
                    isContentSettingsEnabled={enableContentSettingsFeatures}
                    contentSettingsGroups={contentSettingsGroups}
                  />
                ) : (
                  <ContentCard
                    key={index}
                    {...content}
                    isContentSettingsEnabled={enableContentSettingsFeatures}
                    contentSettingsGroups={contentSettingsGroups}
                  />
                );
              },
            )}
          </BookmarkContextProvider>
        </div>

        {isSearchLoading && <LoadingSpinner />}

        {showLoadMoreButton && (
          <ButtonWithTrackEvent
            objectType={objectTypes.content}
            actionName={actionNames.search}
            streamProperties={{
              description: "View more search results on search page",
            }}
            __cssFor={loadMoreButtonCss}
            onClick={() => {
              const [currentPage] = getSearchParamValues(
                LangstonParameterSchema.page.key,
              );
              setSearchParamValue(
                LangstonParameterSchema.page.key,
                `${Number(currentPage) + 1}`,
              );
              fetchNextPage();
            }}
            dataProps={{ "data-qa-selector": "search-view-more" }}
          >
            View more results
          </ButtonWithTrackEvent>
        )}
      </div>
    </>
  );
};

SearchResults.displayName = "SearchResults";
export { SearchResults };
